import { CopyIcon } from "lucide-react";

import { Input } from "@/DesignSystem/shadcn/Input";
import { RegressionMetricInput, CreateMetricInput, FragmentType, gql, useFragment } from "@/apis/nannyml";
import { LabeledField } from "@/components/LabeledField";
import { TextArea } from "@/components/TextArea";
import { Button } from "@/components/common/Button";
import { useCopyToClipboard } from "@/hooks/clipboard";
import { parseNullableFloat } from "@/lib/numbersUtils";

import { MetricConfiguratorProps } from "./types";

const regressionMetricDetails = gql(/* GraphQL */ `
  fragment RegressionMetricDetails on RegressionMetric {
    name
    description
    problemType
    lowerValueLimit
    upperValueLimit
    aggregateFn
    lossFn
  }
`);

const getLossFnTemplate = (metricName: string) => `import numpy as np
import pandas as pd

def loss(y_true: pd.Series, y_pred: pd.Series, chunk_data: pd.DataFrame, **kwargs) -> np.ndarray:
    """Compute loss for '${metricName}' metric"""
    pass
`;

const getAggregateFnTemplate = (metricName: string) => `import numpy as np
import pandas as pd

def aggregate(loss: np.ndarray, chunk_data: pd.DataFrame, **kwargs) -> float:
    """Aggregate (estimated) loss for '${metricName}' metric"""
    pass
`;

export const convertRegressionMetricToInput = (
  fragment: FragmentType<typeof regressionMetricDetails>
): CreateMetricInput => {
  const metric = useFragment(regressionMetricDetails, fragment);
  return {
    name: metric.name,
    description: metric.description,
    problemType: metric.problemType,
    lowerValueLimit: metric.lowerValueLimit,
    upperValueLimit: metric.upperValueLimit,
    regression: {
      aggregateFn: metric.aggregateFn,
      lossFn: metric.lossFn,
    },
  };
};

export const RegressionMetric = ({ metric, onMetricChange }: MetricConfiguratorProps) => {
  const copyToClipboard = useCopyToClipboard();
  const onMetricUpdate = (update: Partial<CreateMetricInput>) => onMetricChange({ ...metric, ...update });
  const onRegressionMetricUpdate = (update: Partial<RegressionMetricInput>) =>
    onMetricChange({ ...metric, regression: { ...metric.regression!, ...update } });

  const lossFnTemplate = getLossFnTemplate(metric.name);
  const aggregateFnTemplate = getAggregateFnTemplate(metric.name);

  return (
    <div className="flex flex-col gap-4">
      <LabeledField label="Metric name" required>
        <Input
          type="text"
          placeholder="What shall we call your metric?"
          value={metric.name}
          onChange={(e) => onMetricUpdate({ name: e.target.value })}
          required
        />
      </LabeledField>
      <LabeledField label="Description">
        <Input
          type="text"
          placeholder="Describe the metric"
          value={metric.description ?? ""}
          onChange={(e) => onMetricUpdate({ description: e.target.value })}
        />
      </LabeledField>
      <LabeledField
        className="grid grid-cols-2 items-end"
        label="Metric loss function"
        infoName="Metric loss function"
        required
      >
        <Button
          cva={{ size: "small", intent: "action" }}
          className="place-self-end"
          onClick={() => copyToClipboard(lossFnTemplate)}
        >
          <CopyIcon size={16} />
          Copy template
        </Button>
        <TextArea
          className="col-span-2"
          cols={100}
          rows={5}
          value={metric.regression?.lossFn ?? ""}
          onChange={(e) => onRegressionMetricUpdate({ lossFn: e.target.value })}
          placeholder={lossFnTemplate}
          required
        />
      </LabeledField>
      <LabeledField
        className="grid grid-cols-2 items-end"
        label="Metric aggregation function"
        infoName="Metric aggregation function"
        required
      >
        <Button
          cva={{ size: "small", intent: "action" }}
          className="place-self-end"
          onClick={() => copyToClipboard(aggregateFnTemplate)}
        >
          <CopyIcon size={16} />
          Copy template
        </Button>
        <TextArea
          className="col-span-2"
          cols={100}
          rows={5}
          value={metric.regression?.aggregateFn ?? ""}
          onChange={(e) => onRegressionMetricUpdate({ aggregateFn: e.target.value })}
          placeholder={aggregateFnTemplate}
          required
        />
      </LabeledField>
      <LabeledField label="Metric limits" infoName="Metric limits">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            className="hide-arrows w-32"
            placeholder="Lower bound"
            value={metric.lowerValueLimit ?? ""}
            onChange={(e) => onMetricUpdate({ lowerValueLimit: parseNullableFloat(e.target.value) })}
          />
          <span>-</span>
          <Input
            type="number"
            className="hide-arrows w-32"
            placeholder="Upper bound"
            value={metric.upperValueLimit ?? ""}
            onChange={(e) => onMetricUpdate({ upperValueLimit: parseNullableFloat(e.target.value) })}
          />
        </div>
      </LabeledField>
    </div>
  );
};
